// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, cloneElement, isValidElement, Component, Fragment, Children, isRtl } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';
import Icon, { ICON_SIZE_16 } from '../Icon';
import Label from '../Label';
import Button from '../Button';
import Dropdown from '../Dropdown';
import Menu, { MenuItem } from '../Menu';
import Tab from './Tab';

import '../../helpers/base.less';
import './Tabs.less';

const ADDON_LEFT_PADDING = 5;
const MIN_ADDON_WIDTH_DEFAULT = 150;
const COLLAPSED_SEARCH_WIDTH = 60;

/**
 * `Tabs` component is a content area with a group of individual [Tab](#!/Tab) elements, each displaying different content
 * in the same screen area. `Tabs` are used when you have too much content to display in one place at the same time, so
 * the content is broken into logical groups and each group is displayed under its own [Tab](#!/Tab) that serves both as a
 * group name and a navigation button.
 */
class Tabs extends Component {
    constructor(props) {
        super(props);
        this.state = {
            active: this.props.active,
            collapsedLength: 0,
            compact: false,
            searching: false,
            monospacedWidth: null,
        };
        this.widths = null;
    }

    getChildContext() {
        return {
            responsiveTabs: this.state.compact,
        };
    }

    componentDidMount() {
        this.isRtl = isRtl();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.active !== this.state.active) {
            this.setState({
                active: nextProps.active,
            });
        }

        if (!this.widths || nextProps.children.length !== this.widths.tabs.length) {
            setTimeout(() => this.handleResize(), 0);
        }
    }

    checkAdaptive = () => {
        let { compact, searching, collapsedLength } = this.state;
        const { addonMinWidth: addonMinWidthProp, search, monospaced } = this.props;
        const { widths, addon } = this;
        let monospacedWidth = null;

        if (!widths) {
            return;
        }

        const addonMinWidth = ADDON_LEFT_PADDING
            + (addonMinWidthProp || (search ? search.props.minWidth : MIN_ADDON_WIDTH_DEFAULT));
        const searchWidth = compact && search ? COLLAPSED_SEARCH_WIDTH + ADDON_LEFT_PADDING : 0;
        const { more, container, margin } = widths;
        let tabs = widths.tabs.slice();
        const tabsNumber = tabs.length;
        let lastTabIndex = tabsNumber - 1 - collapsedLength;

        if (monospaced) {
            monospacedWidth = tabs.slice(0, lastTabIndex + 1).reduce((max, tab) => tab > max ? tab : max, 0);
            tabs = Array(tabsNumber).fill(monospacedWidth);
        }

        const getListWidth = () => {
            let listWidth = margin * Math.max(0, tabsNumber - 1 - Math.max(0, collapsedLength - 1));
            for (let i = 0; i <= lastTabIndex; ++i) {
                listWidth += tabs[i];
            }
            if (collapsedLength) {
                listWidth += more;
            }

            return listWidth;
        };

        let spaceForAddon = container - getListWidth();
        if (addon
            && !collapsedLength
            && !compact
            && spaceForAddon <= addonMinWidth
        ) {
            compact = true;
        }

        let freeSpace = spaceForAddon - searchWidth;

        if (freeSpace <= 0) {
            // collapse
            for (let i = lastTabIndex; i >= 0; --i) {
                let width = tabs[i];

                if (monospaced && widths.tabs[i] === monospacedWidth) {
                    monospacedWidth = widths.tabs.slice(0, lastTabIndex).reduce((max, tab) => tab > max ? tab : max, 0);
                    tabs = Array(tabsNumber).fill(monospacedWidth);
                    width += (widths.tabs[i] - monospacedWidth) * lastTabIndex;
                }

                if (collapsedLength === 0) {
                    width -= more;
                }

                freeSpace += width;
                ++collapsedLength;
                --lastTabIndex;

                if (freeSpace > 0) {
                    break;
                }
            }
        } else {
            // expand
            for (let i = lastTabIndex + 1; i < tabsNumber; ++i) {
                let width = tabs[i];

                if (monospaced && widths.tabs[i] > monospacedWidth) {
                    monospacedWidth = widths.tabs[i];
                    tabs = Array(tabsNumber).fill(monospacedWidth);
                    width = monospacedWidth + ((monospacedWidth - width) * (lastTabIndex + 1));
                }

                if (collapsedLength === 1) {
                    width -= more;
                }

                if (freeSpace <= width) {
                    break;
                }

                freeSpace -= width;
                --collapsedLength;
                ++lastTabIndex;
            }
        }

        if (collapsedLength || compact) {
            spaceForAddon = container - getListWidth();
            if (collapsedLength) {
                compact = true;
            } else if (spaceForAddon >= addonMinWidth) {
                compact = false;
                searching = false;
            }
        }

        const newState = { compact, searching, collapsedLength, monospacedWidth };

        const keys = Object.keys(newState);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (JSON.stringify(newState[key]) === JSON.stringify(this.state[key])) {
                delete newState[key];
            }
        }


        if (Object.keys(newState).length) {
            this.setState(newState, () => {
                if (typeof newState.collapsedLength !== 'undefined'
                    && this.state.collapsedLength !== newState.collapsedLength
                ) {
                    this.checkAdaptive();
                }
            });
        }
    };

    onClick(active) {
        this.setState({ active });
        const tab = this.props.children[active - 1];
        const { onActivate } = tab.props;
        if (onActivate) {
            onActivate();
        }
    }

    handleStartSearching = () => {
        this.setState({ searching: true });
    };

    handleCancelSearching = () => {
        this.setState({ searching: false }, this.checkAdaptive);
    };

    handleResize = () => {
        const { baseClassName } = this.props;

        if (this.tabsnavRef) {
            const tabElements = this.tabsnavRef
                .querySelectorAll(`.${baseClassName}__tab-list--fake .${baseClassName}__tab`);
            const more = tabElements[tabElements.length - 1];
            const tabs = Array.prototype.slice.call(tabElements, 0, -1).map(el => el.offsetWidth);
            this.widths = {
                tabs,
                more: more.offsetWidth,
                container: this.tabsnavRef.offsetWidth,
                margin: more.offsetWidth - more.querySelector(`.${baseClassName}__link`).offsetWidth,
            };
        }

        this.checkAdaptive();
    };

    handleAddonResize = contentRect => {
        this.addon = contentRect.bounds;
        this.checkAdaptive();
    };

    renderTabLabel(tabMode, label) {
        const { baseClassName } = this.props;
        if (typeof label === 'string') {
            return (
                <Label className={`${baseClassName}__${tabMode}-label`} intent="danger">
                    {label}
                </Label>
            );
        }
        if (isValidElement(label)) {
            return cloneElement(label, {
                className: classNames(
                    `${baseClassName}__${tabMode}-label`,
                    label.props.className,
                ),
            });
        }
        return null;
    }

    renderTabClose(tabMode, onClose) {
        const { baseClassName } = this.props;
        if (typeof onClose === 'function') {
            return (
                <button className={`${baseClassName}__${tabMode}-close`} type="button" onClick={onClose}>
                    <Icon className={`${baseClassName}__${tabMode}-close-icon`} name="cross-mark" />
                </button>
            );
        }
        return null;
    }

    renderFakeTabs() {
        const { baseClassName, children } = this.props;

        const childrenCount = Children.count(children);
        if (!childrenCount) {
            return null;
        }

        return (
            <ul
                className={classNames(
                    `${baseClassName}__tab-list`,
                    `${baseClassName}__tab-list--fake`,
                )}
            >
                {Children.map(children, tab => {
                    if (!tab || tab.type !== Tab) {
                        return null;
                    }
                    const { title, label, icon, onActivate, onClose, ...tabProps } = tab.props;
                    const iconBlock = Icon.create(icon, {
                        size: ICON_SIZE_16,
                        className: `${baseClassName}__tab-icon`,
                    });

                    const closeBlock = this.renderTabClose('tab', onClose);

                    return (
                        <li
                            key={title}
                            className={classNames(
                                `${baseClassName}__tab`,
                                closeBlock && `${baseClassName}__tab--closable`,
                            )}
                        >
                            <a
                                className={`${baseClassName}__link`}
                                tabIndex="-1"
                                style={tabProps.style}
                            >
                                {iconBlock}
                                <span className={`${baseClassName}__tab-title`}>
                                    {title}
                                </span>
                                {this.renderTabLabel('tab', label)}
                            </a>
                            {closeBlock}
                        </li>
                    );
                })}

                <li
                    className={classNames(
                        `${baseClassName}__tab`,
                        `${baseClassName}__tab--more`,
                    )}
                >
                    <a
                        className={`${baseClassName}__link`}
                        tabIndex="-1"
                    >
                        <Icon name="three-dots-horizontal" />
                    </a>
                </li>
            </ul>
        );
    }

    renderTablist() {
        const {
            baseClassName,
            children,
        } = this.props;
        const { collapsedLength, monospacedWidth } = this.state;
        let moreTabs = null;
        let moreSelected = false;

        const childrenCount = Children.count(children);
        if (!childrenCount) {
            return null;
        }
        const lastTabIndex = childrenCount - 1 - collapsedLength;

        return (
            <ul className={`${baseClassName}__tab-list`} role="tablist">
                {Children.map(children, (tab, index) => {
                    if (!tab || tab.type !== Tab) {
                        return null;
                    }
                    const { title, label, icon, onActivate, onClose, ...tabProps } = tab.props;
                    const selected = index + 1 === this.state.active;
                    const iconBlock = Icon.create(icon, {
                        size: ICON_SIZE_16,
                        className: `${baseClassName}__tab-icon`,
                    });

                    if (index > lastTabIndex) {
                        if (selected) {
                            moreSelected = true;
                        }

                        moreTabs = moreTabs || [];
                        moreTabs.push((
                            <MenuItem
                                key={moreTabs.length}
                                icon={iconBlock}
                                label={
                                    <Fragment>
                                        {this.renderTabLabel('menu-item', label)}
                                        {this.renderTabClose('menu-item', onClose)}
                                    </Fragment>
                                }
                                active={selected}
                                onClick={() => this.onClick(index + 1)}
                            >
                                {title}
                            </MenuItem>
                        ));

                        return null;
                    }

                    const closeBlock = this.renderTabClose('tab', onClose);

                    return (
                        <li
                            key={title}
                            className={classNames(
                                `${baseClassName}__tab`,
                                selected && `${baseClassName}__tab--selected`,
                                closeBlock && `${baseClassName}__tab--closable`,
                            )}
                            role="presentation"
                        >
                            <a
                                className={`${baseClassName}__link`}
                                onClick={() => this.onClick(index + 1)}
                                role="tab"
                                tabIndex="0"
                                aria-selected={selected || null}
                                style={{ width: monospacedWidth }}
                                {...tabProps}
                            >
                                {iconBlock}
                                <span className={`${baseClassName}__tab-title`}>
                                    {title}
                                </span>
                                {this.renderTabLabel('tab', label)}
                            </a>
                            {closeBlock}
                        </li>
                    );
                })}

                {moreTabs && (
                    <li
                        ref={ref => (this.moreTabsRef = ref)}
                        className={classNames(`${baseClassName}__tab`, moreSelected && `${baseClassName}__tab--selected`, `${baseClassName}__tab--more`)}
                        role="presentation"
                        aria-selected={moreSelected || null}
                    >
                        <Dropdown className={`${baseClassName}__dropdown`} menu={<Menu>{moreTabs}</Menu>} menuPlacement="bottom-end">
                            <a
                                className={`${baseClassName}__link`}
                                role="tab"
                                tabIndex="0"
                                aria-selected={moreSelected || null}
                            >
                                <Icon name="three-dots-horizontal" />
                            </a>
                        </Dropdown>
                    </li>
                )}
            </ul>
        );
    }

    getSearchCompactProps(searchProps) {
        const { searching } = this.state;

        if (searching) {
            searchProps.maxWidth = null;
            searchProps.minWidth = null;
            searchProps.autoFocus = true;
            searchProps.responsive = false;
            searchProps.leftAddon = (
                <Button
                    icon={{ name: 'chevron-right', flipHorizontal: this.isRtl }}
                    onClick={this.handleCancelSearching}
                />
            );
        } else {
            searchProps.leftAddon = (
                <Button
                    icon={{ name: 'chevron-left', flipHorizontal: this.isRtl }}
                    onClick={this.handleStartSearching}
                />
            );
            searchProps.onSearch = this.handleStartSearching;
        }

        return searchProps;
    }

    render() {
        const {
            baseClassName,
            className,
            addon,
            placeholder,
            children,
            active,
            addonMinWidth,
            monospaced,
            search,
            ...props
        } = this.props;
        const { compact, searching } = this.state;

        const childrenCount = Children.count(children);
        if (!childrenCount) {
            return null;
        }

        let addonBlock = addon || placeholder;
        const addonProps = {};
        let searchBlock = null;
        if (search) {
            const searchProps = {
                className: classNames(
                    `${baseClassName}__search`,
                    search.props.className,
                ),
            };

            if (compact) {
                searchBlock = cloneElement(search, this.getSearchCompactProps(searchProps));
            } else if (addonBlock) {
                searchProps.key = 1;
                addonBlock = [cloneElement(search, searchProps), cloneElement(addonBlock, { key: 2 })];
            } else {
                addonProps.style = { maxWidth: search.props.maxWidth };
                addonBlock = cloneElement(search, searchProps);
            }
        }

        return (
            <div className={classNames(baseClassName, className)} {...props}>
                <Measure onResize={this.handleResize} innerRef={ref => (this.tabsnavRef = ref)}>
                    {({ measureRef }) => (
                        <div
                            className={classNames(
                                `${baseClassName}__nav`,
                                compact && `${baseClassName}__nav--compact`,
                                searching && `${baseClassName}__nav--searching`,
                            )}
                            ref={measureRef}
                        >
                            {this.renderTablist()}
                            <Measure onResize={this.handleResize}>
                                {({ measureRef: ref }) => cloneElement(this.renderFakeTabs(), { ref })}
                            </Measure>

                            {searchBlock}

                            {addonBlock && (
                                <Measure onResize={this.handleAddonResize} bounds>
                                    {({ measureRef: ref }) => (
                                        <div className={`${baseClassName}__addon`} ref={ref} {...addonProps}>
                                            {addonBlock}
                                        </div>
                                    )}
                                </Measure>
                            )}
                        </div>
                    )}
                </Measure>
                <div className={`${baseClassName}__tab-panel`} role="tabpanel">
                    {Children.map(children, (tab, index) => index + 1 === this.state.active ? tab : null)}
                </div>
            </div>
        );
    }
}

Tabs.propTypes = {
    /**
     * Number of active tab
     */
    active: PropTypes.number,
    /**
     * Addon for search bar, dropdown etc.
     */
    addon: PropTypes.element,
    /**
     * Placeholder for search bar, dropdown etc.
     *
     * @deprecated Do not use! Use `addon` instead!
     */
    placeholder: PropTypes.element,
    /**
     * Minimum add-on width in pixels
     */
    addonMinWidth: PropTypes.number,
    /**
     * Monospaced version of tabs (all visible tabs as wide as widest of them)
     */
    monospaced: PropTypes.bool,
    /**
     * [SearchBar](#!/SearchBar) element
     */
    search: PropTypes.element,
    /**
     * [Tabs](#!/Tab)
     */
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.element),
        PropTypes.element,
    ]),
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

Tabs.defaultProps = {
    active: 1,
    addon: null,
    placeholder: null,
    addonMinWidth: null,
    monospaced: null,
    search: null,
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}tabs`,
};

Tabs.childContextTypes = {
    responsiveTabs: PropTypes.bool,
};

export default Tabs;



// WEBPACK FOOTER //
// ./components/Tabs/Tabs.js